import { OACData } from '@/modules/pipelines/model/oac-data';
import { ChartOptions } from 'chart.js';
import dayjs from 'dayjs';
import { Chart, Line } from 'react-chartjs-2';

export default function OACChartPane({ oacHistory }: { oacHistory: OACData[] }) {
  const dcDataset = {
    label: 'DC',
    data: oacHistory.map(h => h.designCap),
    borderColor: 'rgb(192, 75, 75)',
    backgroundColor: 'rgba(192, 75, 75, 0.5)',
  };
  const opcDataset = {
    label: 'OPC',
    data: oacHistory.map(h => h.operatingCap),
    borderColor: 'rgb(192, 75, 192)',
    backgroundColor: 'rgba(192, 75, 192, 0.5)',
  };
  const tsqDataset = {
    label: 'TSQ',
    data: oacHistory.map(h => h.scheduledCap),
    borderColor: 'rgb(192, 192, 75)',
    backgroundColor: 'rgba(192, 192, 75, 0.5)',
  };
  const oacDataset = {
    label: 'OAC',
    data: oacHistory.map(h => h.availableCap),
    borderColor: 'rgb(75, 192, 192)',
    backgroundColor: 'rgba(75, 192, 192, 0.5)',
  };

  const options: ChartOptions<'bar' | 'line'> = {
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return new Intl.NumberFormat('en-US', {
              notation: 'compact',
              compactDisplay: 'short',
            }).format(Number(value));
          },
        },
      },
    },
  };

  const labels = oacHistory.map(h => `${h.cycle}, ${dayjs(h.gasDay).utc().format('MMM Do')}`);

  return (
    <>
      <span className="w-50">
        <Line
          data={{
            labels,
            datasets: [dcDataset, opcDataset, tsqDataset, oacDataset],
          }}
          options={options}
        />
      </span>

      <span className="w-50">
        <Chart
          type="bar"
          plugins={[
            {
              id: 'designCapacityLine',
              afterDraw(chart) {
                const {
                  ctx,
                  chartArea: { left, right },
                  scales: { x, y },
                } = chart;

                const designCapIndex = chart.data.datasets.findIndex(ds => ds.label === 'DC');
                const designCap = chart.data.datasets[designCapIndex];
                if (!designCap?.data || designCap?.data.length < 1) return;
                if (!chart.isDatasetVisible(designCapIndex)) return;

                const firstVal = designCap.data[0];
                const lastVal = designCap.data[designCap.data.length - 1];
                if (firstVal === null || lastVal === null) return;

                const xFPixel = x.getPixelForValue(0);
                const xLPixel = x.getPixelForValue(designCap.data.length - 1);

                const yFPixel = y.getPixelForValue(firstVal as number);
                const yLPixel = y.getPixelForValue(lastVal as number);

                ctx.save();

                ctx.beginPath();
                ctx.lineWidth = 2;
                ctx.strokeStyle = 'red';
                ctx.setLineDash([5, 5]);
                ctx.moveTo(left, yFPixel);
                ctx.lineTo(xFPixel, yFPixel);
                ctx.stroke();

                ctx.beginPath();
                ctx.lineWidth = 2;
                ctx.strokeStyle = 'red';
                ctx.setLineDash([5, 5]);
                ctx.moveTo(xLPixel, yLPixel);
                ctx.lineTo(right, yLPixel);
                ctx.stroke();

                ctx.restore();
              },
            },
          ]}
          data={{
            labels,
            datasets: [
              tsqDataset,
              oacDataset,
              {
                type: 'line',
                label: 'DC',
                data: oacHistory.map(h => h.designCap),
                borderColor: 'red',
                borderWidth: 2,
                borderDash: [5, 5],
                pointRadius: 0,
                order: 0,
              },
            ],
          }}
          options={{
            ...options,
            scales: {
              ...options.scales,
              x: { stacked: true },
              y: { ...options.scales?.y, stacked: true },
            },
          }}
        />
      </span>
    </>
  );
}
